import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button, Card, Pill, Spinner } from "@/components/ui-kit";
import QuestionCard from "@/components/QuestionCard";
import MathText from "@/components/MathText";
import { RotateCw, Zap, Layers, Timer, Check } from "lucide-react";

export default function Review() {
  const [tab, setTab] = useState("flashcards");
  return (
    <div>
      <h1 className="font-head text-3xl font-bold mb-1">Review</h1>
      <p className="text-[var(--ns-muted)] mb-5">Spaced repetition keeps mastered skills sharp.</p>
      <div className="flex gap-2 mb-5 overflow-x-auto ns-scroll">
        {[["flashcards", "Flashcards", Layers], ["retention", "Retention", RotateCw], ["drill", "Mental math", Zap]].map(([id, label, Icon]) => (
          <button key={id} data-testid={`review-tab-${id}`} onClick={() => setTab(id)}
            className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors"
            style={{ background: tab === id ? "var(--ns-primary)" : "var(--ns-surface)", color: tab === id ? "#fff" : "var(--ns-muted)", border: "1px solid var(--ns-border)" }}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>
      {tab === "flashcards" && <Flashcards />}
      {tab === "retention" && <Retention />}
      {tab === "drill" && <Drill />}
    </div>
  );
}

function Flashcards() {
  const { studentId } = useOutletContext();
  const [cards, setCards] = useState(null);
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);

  const load = () => api.get(`/students/${studentId}/review`).then(({ data }) => { setCards(data.flashcards); setI(0); setFlipped(false); });
  useEffect(() => { load(); }, [studentId]);
  if (!cards) return <Spinner label="Loading cards" />;
  if (cards.length === 0) return <Empty text="No flashcards due right now. Come back later!" />;
  if (i >= cards.length) return <Done text="Flashcards done for now!" onReload={load} />;

  const card = cards[i];
  const rate = async (rating) => {
    await api.post(`/students/${studentId}/review/flashcard`, { card_id: card.id, rating });
    setFlipped(false); setI(i + 1);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 text-sm text-[var(--ns-muted)]">
        <Pill tone="primary">{card.skill_name}</Pill><span>{i + 1} / {cards.length}</span>
      </div>
      <motion.div key={i} initial={{ opacity: 0, rotateY: -8 }} animate={{ opacity: 1, rotateY: 0 }}>
        <Card className="p-10 min-h-[220px] grid place-items-center text-center cursor-pointer" data-testid="flashcard" onClick={() => setFlipped(!flipped)}>
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--ns-subtle)] mb-3">{flipped ? "Answer" : "Question"}</div>
            <div className="font-head text-2xl font-semibold"><MathText text={flipped ? card.back : card.front} /></div>
            {!flipped && <div className="mt-6 text-sm text-[var(--ns-primary)] font-semibold">Tap to reveal</div>}
          </div>
        </Card>
      </motion.div>
      {flipped && (
        <div className="mt-5 grid grid-cols-4 gap-2" data-testid="rating-row">
          {[[1, "Again", "#E05A47"], [2, "Hard", "#D96B43"], [3, "Good", "#2A8555"], [4, "Easy", "#1E6B52"]].map(([r, l, c]) => (
            <button key={r} data-testid={`rate-${r}`} onClick={() => rate(r)} className="rounded-[var(--ns-radius)] py-3 text-white text-sm font-semibold" style={{ background: c }}>{l}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function Retention() {
  const { studentId, reload } = useOutletContext();
  const [checks, setChecks] = useState(null);
  const [active, setActive] = useState(null);

  const load = () => api.get(`/students/${studentId}/review`).then(({ data }) => setChecks(data.retention_checks));
  useEffect(() => { load(); }, [studentId]);
  if (!checks) return <Spinner label="Loading retention checks" />;
  if (active) return <RetentionCheck skillId={active.skill_id} name={active.skill_name} studentId={studentId}
    onDone={() => { setActive(null); load(); reload(); }} />;
  if (checks.length === 0) return <Empty text="No 7-day retention checks due. Mastered skills are still fresh!" />;

  return (
    <div className="grid gap-3">
      {checks.map((c) => (
        <Card key={c.skill_id} className="p-4 flex items-center justify-between" data-testid={`retention-${c.skill_id}`}>
          <div>
            <div className="font-head font-semibold">{c.skill_name}</div>
            <div className="text-xs text-[var(--ns-subtle)]">Retention check due</div>
          </div>
          <Button variant="subtle" onClick={() => setActive(c)}>Check</Button>
        </Card>
      ))}
    </div>
  );
}

function RetentionCheck({ skillId, name, studentId, onDone }) {
  const [items, setItems] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.post(`/students/${studentId}/practice/start`, { skill_id: skillId }).then(({ data }) => setItems(data.items.slice(0, 3))).catch(() => {}); }, [skillId]);
  if (!items) return <Spinner label="Loading check" />;
  const cur = items[idx];

  const finish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/students/${studentId}/practice/submit`, {
        skill_id: skillId, answers: items.map((it) => ({ item_id: it.item_id, answer: answers[it.item_id] || "" })),
      });
      const passed = data.correct / data.total >= 0.67;
      await api.post(`/students/${studentId}/review/retention`, { skill_id: skillId, passed });
      toast[passed ? "success" : "warning"](passed ? "Still mastered — great retention!" : "Back into review — we'll revisit this soon.");
      onDone();
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <Pill tone="primary">{name}</Pill><span className="text-sm text-[var(--ns-muted)]">{idx + 1}/{items.length}</span>
      </div>
      <Card className="p-6"><QuestionCard item={cur} value={answers[cur.item_id] || ""} onChange={(v) => setAnswers({ ...answers, [cur.item_id]: v })} /></Card>
      <Button className="mt-5 w-full" data-testid="retention-next" disabled={busy || (answers[cur.item_id] ?? "") === ""}
        onClick={() => (idx < items.length - 1 ? setIdx(idx + 1) : finish())}>
        {idx < items.length - 1 ? "Next" : busy ? "Checking…" : "Finish check"}
      </Button>
    </div>
  );
}

function Drill() {
  const { studentId } = useOutletContext();
  const [problems, setProblems] = useState(null);
  const [i, setI] = useState(0);
  const [val, setVal] = useState("");
  const [correct, setCorrect] = useState(0);
  const [start, setStart] = useState(null);
  const [done, setDone] = useState(null);
  const inputRef = useRef();

  const begin = () => api.get(`/students/${studentId}/drill`).then(({ data }) => {
    setProblems(data.problems); setI(0); setCorrect(0); setVal(""); setDone(null); setStart(Date.now());
  }).catch(() => {});

  const answer = async () => {
    const p = problems[i];
    const ok = String(val).trim() === p.answer;
    const newCorrect = correct + (ok ? 1 : 0);
    setCorrect(newCorrect);
    setVal("");
    if (i < problems.length - 1) { setI(i + 1); inputRef.current?.focus(); }
    else {
      const seconds = Math.round((Date.now() - start) / 1000);
      const { data } = await api.post(`/students/${studentId}/drill/submit`, { correct: newCorrect, total: problems.length, duration_sec: seconds });
      setDone(data);
    }
  };

  if (!problems) return (
    <Card className="p-8 text-center" data-testid="drill-intro">
      <Zap size={40} className="mx-auto text-[var(--ns-gold)]" />
      <h3 className="font-head text-xl font-bold mt-3">Mental math sprint</h3>
      <p className="text-[var(--ns-muted)] mt-1">10 quick facts. Speed and accuracy both count.</p>
      <Button className="mt-5 mx-auto" data-testid="start-drill-btn" onClick={begin}>Start sprint</Button>
    </Card>
  );

  if (done) return (
    <Card className="p-8 text-center" data-testid="drill-result">
      <div className="font-head text-4xl font-bold text-[var(--ns-primary)]">{done.accuracy}%</div>
      <p className="text-[var(--ns-muted)] mt-1">{done.correct}/{done.total} correct · {done.per_minute}/min</p>
      <Button className="mt-5 mx-auto" onClick={begin}><RotateCw size={16} /> Go again</Button>
    </Card>
  );

  const p = problems[i];
  return (
    <div>
      <div className="flex items-center justify-between mb-3 text-sm text-[var(--ns-muted)]"><Pill tone="gold"><Timer size={14} /> Sprint</Pill><span>{i + 1}/{problems.length}</span></div>
      <Card className="p-10 text-center">
        <div className="font-head text-4xl font-bold" data-testid="drill-problem">{p.prompt} =</div>
        <input ref={inputRef} autoFocus data-testid="drill-input" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && val && answer()}
          className="mt-6 w-40 mx-auto text-center rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] px-4 py-3 text-2xl focus:outline-none focus:border-[var(--ns-primary)]" />
      </Card>
      <Button className="mt-5 w-full" data-testid="drill-submit" disabled={!val} onClick={answer}><Check size={16} /> {i < problems.length - 1 ? "Next" : "Finish"}</Button>
    </div>
  );
}

const Empty = ({ text }) => <Card className="p-10 text-center text-[var(--ns-muted)]" data-testid="review-empty">{text}</Card>;
const Done = ({ text, onReload }) => <Card className="p-10 text-center"><p className="text-[var(--ns-muted)]">{text}</p><Button className="mt-4 mx-auto" onClick={onReload}>Refresh</Button></Card>;
